import React, { useState } from 'react';
import dayjs from 'dayjs';
import {
  Box,
  IconButton,
  Typography,
  Dialog,
  Button,
  DialogActions,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { StaticDateRangePicker } from '@mui/x-date-pickers-pro/StaticDateRangePicker';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import HeightOutlined from '@mui/icons-material/HeightOutlined';
import useDateFormat from '../../hooks/useDateFormat';

const shortcutsItems = [
  {
    label: 'Current Quarter',
    getValue: () => {
      const today = dayjs();
      const startOfQuarter = today.startOf('quarter');
      const endOfQuarter = today.endOf('quarter');
      return [startOfQuarter, endOfQuarter];
    },
  },
  {
    label: 'Last 6 Months',
    getValue: () => {
      const today = dayjs();
      const startOfLast6Months = today.subtract(6, 'month').startOf('month');
      return [startOfLast6Months, today];
    },
  },
  {
    label: 'Current Year',
    getValue: () => {
      const today = dayjs();
      return [today.startOf('year'), today.endOf('year')];
    },
  },
  {
    label: '1 Year',
    getValue: () => {
      const today = dayjs();
      return [today.subtract(1, 'year'), today];
    },
  },
  { label: 'Reset', getValue: () => [null, null] },
];

const DateRangePickerWithShortcuts = ({
  label,
  startDate,
  endDate,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const [selectedDates, setSelectedDates] = useState([startDate, endDate]);
  const { dateFormat } = useDateFormat();

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleApply = () => {
    onChange(selectedDates);
    handleClose();
  };

  const handleCancel = () => {
    setSelectedDates([startDate, endDate]);
    handleClose();
  };

  const handleDateChange = (newValue) => {
    setSelectedDates(newValue);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        borderColor: open ? 'primary.main' : 'divider',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderRadius: '4px',
        padding: '10px',
      }}
    >
      <IconButton onClick={handleOpen}>
        <CalendarMonthOutlinedIcon />
      </IconButton>
      <Box sx={{ display: 'flex', flexDirection: 'column', marginLeft: 1 }}>
        <Typography variant='body1'>
          {startDate ? startDate.format(dateFormat) : 'Start Date'}
        </Typography>
        <Typography variant='body1'>
          {endDate ? endDate.format(dateFormat) : 'End Date'}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginLeft: 1,
        }}
      >
        <HeightOutlined />
      </Box>
      <Dialog open={open} onClose={handleClose} maxWidth={'md'}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <StaticDateRangePicker
            value={selectedDates}
            onChange={handleDateChange}
            slotProps={{
              shortcuts: {
                items: shortcutsItems,
              },
              actionBar: { actions: [] },
            }}
            calendars={2}
          />
        </LocalizationProvider>
        <DialogActions>
          <Button onClick={handleCancel} color='secondary'>
            Cancel
          </Button>
          <Button onClick={handleApply} color='primary'>
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DateRangePickerWithShortcuts;
